import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// Design System Components
import Menu from 'Components/DesignSystem/Menu/Menu';

// Types
import { RowActionsMenuProps, RowActionType } from '../../types';

// Three-dot icon
const ThreeDotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
);

/**
 * RowActionsMenu Component
 *
 * Displays a 3-dot menu button that opens a dropdown with row actions
 * Used in table rows for individual row actions
 */
// Rough height used to decide whether to flip the dropdown upward.
// Covers up to ~4 menu items — slightly conservative so we flip sooner rather
// than clip. Exact height doesn't matter; we just need a reasonable threshold.
const APPROX_MENU_HEIGHT_PX = 180;

// Spacing between the trigger button and the dropdown (matches the previous
// `tw-mt-1` / `tw-mb-1` gap).
const TRIGGER_GAP_PX = 4;
// Minimum width for the portaled dropdown (keeps it from shrinking below the
// three-dot button width when nothing's placed inside yet).
const DROPDOWN_MIN_WIDTH_PX = 150;

const RowActionsMenu = ({
  actions,
  onAction,
  disabled = false,
  className = '',
}: RowActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // Position of the portaled dropdown, in viewport coordinates.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside — include the portaled dropdown in the
  // "inside" check so clicks on menu items don't immediately dismiss it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInTrigger = containerRef.current?.contains(target);
      const clickedInDropdown = dropdownRef.current?.contains(target);
      if (!clickedInTrigger && !clickedInDropdown) {
        setIsOpen(false);
      }
    };
    // Any scroll or resize moves the trigger; close rather than try to chase it.
    const handleDismiss = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleDismiss, true);
      window.addEventListener('resize', handleDismiss);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleDismiss, true);
        window.removeEventListener('resize', handleDismiss);
      };
    }
  }, [isOpen]);

  // Transform actions for Menu format
  const menuItems = actions.map((action) => ({
    id: action.type,
    label: action.label,
    icon: action.icon,
    disabled: action.disabled,
  }));

  const handleItemClick = (itemId: string | number) => {
    onAction(itemId as RowActionType);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (disabled) return;
    // Portal-position the dropdown: compute where it should appear in viewport
    // coords, flipping upward when there isn't room below. Because the dropdown
    // is rendered to document.body, no ancestor's `overflow:auto` can clip it
    // (the whole reason rows at the bottom of the scrollable table previously
    // only showed a partial menu).
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < APPROX_MENU_HEIGHT_PX && spaceAbove > spaceBelow;
      const top = openUp
        ? rect.top - TRIGGER_GAP_PX - APPROX_MENU_HEIGHT_PX
        : rect.bottom + TRIGGER_GAP_PX;
      const right = window.innerWidth - rect.right;
      setMenuPos({ top, right });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={containerRef} className={`tw-relative tw-inline-block ${className}`}>
      {/* Trigger Button */}
      <button data-testid="table-rowactionsmenu-button-1"
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`tw-p-2 tw-rounded tw-transition-colors ${disabled ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink'}`}
        aria-label="Row actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ThreeDotsIcon />
      </button>

      {/* Dropdown Menu — portaled to document.body so nothing in the table's
          overflow:auto wrapper can clip it, and so it sits above any stacking
          context. Positioned in viewport coords computed at open time. */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            minWidth: DROPDOWN_MIN_WIDTH_PX,
            zIndex: 9999,
          }}
        >
          <Menu
            items={menuItems}
            onItemClick={handleItemClick}
            variant="compact"
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

export default RowActionsMenu;
